import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { AlertCircle } from 'lucide-react';
import { useQuestionnaireSocket } from '../hooks/useQuestionnaireSocket';
import TeamQuestionnaireDisplay from './questionnaire/TeamQuestionnaireDisplay';
import ProgressIndicator from './questionnaire/ProgressIndicator';
import Navigation from './questionnaire/Navigation';
import ReviewMode from './questionnaire/ReviewMode';
import { getDefaultValue, isResponseComplete } from '../utils/questionUtils';
import { CURRENCY_CONFIGS } from '../utils/currencyConfigs';
import { authenticatedFetch } from '../utils/apiUtils';
import questionnaireService from '../services/questionnaireService';

// Define load states outside component to avoid recreation
const LOAD_STATE = {
    INITIAL: 'initial',
    LOADING: 'loading',
    LOADED: 'loaded',
    ERROR: 'error'
};

const RequirementsQuestionnaire = ({
    organization,
    onComplete = () => {},
    onUpdateResponses = () => {},
    startInReviewMode = false,
    initialResponses = {},
    user,
    hideTeamSection = false
}) => {
    // Validation for required props
    if (!organization) {
        console.error('RequirementsQuestionnaire: organization prop is required');
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <AlertCircle className="w-8 h-8 text-red-500" />
                <div className="text-lg text-red-600">Organization data is required</div>
            </div>
        );
    }

    // Safe currency access with fallback - memoized
    const currency = useMemo(() =>
        CURRENCY_CONFIGS[organization?.currency || 'USD'] || CURRENCY_CONFIGS.USD,
        [organization?.currency]
    );

    // Core state
    const [loadState, setLoadState] = useState(LOAD_STATE.INITIAL);
    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showReview, setShowReview] = useState(false);
    const [hasCompletedOnce, setHasCompletedOnce] = useState(false);
    const [responses, setResponses] = useState(initialResponses);
    
    // Refs for tracking load state
    const isMountedRef = useRef(true);
    const initialLoadComplete = useRef(false);
    const hasInitialResponses = useRef(Object.keys(initialResponses).length > 0);
    const loadAttempts = useRef(0);

    // Response handling state
    const [organizationResponses, setOrganizationResponses] = useState({});
    const [tempResponses, setTempResponses] = useState({});
    const [validationErrors, setValidationErrors] = useState({});

    // Team collaboration state
    const [teamResponses, setTeamResponses] = useState({});
    const [comments, setComments] = useState({});
    const [questionComments, setQuestionComments] = useState({});
    const [reactions, setReactions] = useState({});
    const [teamMembers, setTeamMembers] = useState([]);
    const [lockedResponses, setLockedResponses] = useState({});
    const [currentUser, setCurrentUser] = useState(null);
    const [userWithRole, setUserWithRole] = useState(user ? {
        ...user,
        role: null
    } : null);

    // Effect to get user's role in the organization
    useEffect(() => {
        const fetchUserRole = async () => {
            if (!organization?.id || !user?.id) {
                console.log('Missing required data for role fetch:', {
                    organizationId: organization?.id,
                    userId: user?.id
                });
                return;
            }

            try {
                console.log('Fetching org members for:', organization.id);
                const memberResponse = await authenticatedFetch(
                    `/api/organizations/${organization.id}/members`,
                    { 
                        headers: {
                            'Accept': 'application/json'
                        }
                    }
                );

                if (!memberResponse.ok) {
                    const errorData = await memberResponse.json();
                    console.error('Member fetch failed:', errorData);
                    throw new Error(errorData.error || 'Failed to fetch organization members');
                }

                const members = await memberResponse.json();
                console.log('Received members:', members);

                const currentMember = members.find(m => m.user_id === user.id);
                console.log('Current member data:', currentMember);

                if (currentMember) {
                    setUserWithRole(prev => ({
                        ...prev,
                        role: currentMember.role
                    }));
                    setTeamMembers(members);
                } else {
                    console.warn('User not found in organization members');
                    setUserWithRole(prev => ({
                        ...prev,
                        role: 'member' // Default fallback
                    }));
                }
            } catch (error) {
                console.error('Error fetching user role:', error);
                setUserWithRole(prev => ({
                    ...prev,
                    role: 'member' // Default to member if we can't determine role
                }));
            }
        };

  fetchUserRole();
}, [organization?.id, user?.id]);



  // Socket callback handlers
  const handleTeamResponseUpdate = useCallback((questionId, response) => {
    if (!questionId || !response) {
        console.warn('Invalid team response update:', { questionId, response });
        return;
    }

    console.log('Received team response update:', { questionId, response });

    setTeamResponses(prev => {
        // Create a new array for this question if it doesn't exist
        const existingResponses = prev[questionId] || [];
        
        // Filter out any old response from this user
        const filteredResponses = existingResponses.filter(r => 
            r.user_id !== response.user_id
        );
        
        // Add the new response
        const updatedResponses = [...filteredResponses, response];
        
        console.log('Updated team responses for question:', {
            questionId,
            responses: updatedResponses
        });

        return {
            ...prev,
            [questionId]: updatedResponses
        };
    });
}, []);

  const handleCommentAdd = useCallback((questionId, comment) => {
    if (!questionId || !comment) {
      console.warn('Invalid comment data:', { questionId, comment });
      return;
    }
    setComments(prev => ({
      ...prev,
      [questionId]: [...(prev[questionId] || []), comment]
    }));
  }, []);

  const handleReactionAdd = useCallback((questionId, responseId, reaction) => {
    if (!questionId || !responseId || !reaction) {
      console.warn('Invalid reaction data:', { questionId, responseId, reaction });
      return;
    }
    setReactions(prev => ({
      ...prev,
      [`${questionId}-${responseId}`]: [
        ...(prev[`${questionId}-${responseId}`] || []),
        reaction
      ]
    }));
  }, []);

  const handleResponseLock = useCallback((questionId, userId) => {
    if (!questionId || !userId) {
      console.warn('Invalid lock data:', { questionId, userId });
      return;
    }
    setLockedResponses(prev => ({
      ...prev,
      [questionId]: userId
    }));
  }, []);

  // Socket configuration
  const socketConfig = useMemo(() => ({
    organizationId: organization?.id,
    onTeamResponseUpdate: handleTeamResponseUpdate,
    onCommentAdd: handleCommentAdd,
    onReactionAdd: handleReactionAdd,
    onResponseLock: handleResponseLock,
    mounted: isMountedRef.current
  }), [
    organization?.id,
    handleTeamResponseUpdate,
    handleCommentAdd,
    handleReactionAdd,
    handleResponseLock,
    isMountedRef.current
  ]);

  const {
    emitTeamResponse,
    emitComment,
    emitReaction,
    emitLock,
    error: socketError
  } = useQuestionnaireSocket(socketConfig);

  // Set error if socket fails
  useEffect(() => {
    if (socketError) {
      setError(prev => prev || `Socket connection error: ${socketError}`);
    }
  }, [socketError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  // Helper function to parse response values based on question type
  const parseResponseValue = (value, questionType) => {
    if (!value) return null;

    try {
      switch (questionType) {
        case 'multiple_choice':
          if (typeof value === 'string') {
            return JSON.parse(value);
          }
          return Array.isArray(value) ? value : [value];

        case 'range':
          if (typeof value === 'string') {
            return JSON.parse(value);
          }
          return value;

        case 'tristate_boolean':
          return String(value).toUpperCase();

        case 'boolean':
          return value === 'true' || value === true;

        default:
          return value;
      }
    } catch (e) {
      console.error('Error parsing response value:', e, { value, questionType });
      return value;
    }
  };

  // Load current user info
  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await authenticatedFetch('/api/auth/me');
      if (!response.ok) {
        throw new Error('Failed to fetch user data');
      }
      const userData = await response.json();
      setCurrentUser(userData);
    } catch (error) {
      console.error('Error fetching current user:', error);
      setError('Failed to load user data');
    }
  }, []);

  // Core data loading function
// In RequirementsQuestionnaire.jsx

// In RequirementsQuestionnaire.jsx

// In RequirementsQuestionnaire.jsx

// Update the loadData function
const loadData = useCallback(async () => {
  if (!organization?.id || loadState !== LOAD_STATE.INITIAL) {
    console.warn('Skipping data load:', { 
      orgId: organization?.id, 
      loadState,
      attempts: loadAttempts.current 
    });
    return;
  }

  try {
    setLoadState(LOAD_STATE.LOADING);
    console.log('Starting data load for org:', organization.id);

    // Add members fetch to the Promise.all array
    const [questionsResponse, userResponsesResponse, statusResponse, membersResponse] = await Promise.all([
      authenticatedFetch('/api/core-questions', {
        headers: { 'Cache-Control': 'no-cache' }
      }),
      authenticatedFetch(`/api/core-questions/responses/${organization.id}/team`, {
        headers: { 'Cache-Control': 'no-cache' }
      }),
      authenticatedFetch(`/api/core-questions/completion-status/${organization.id}`, {
        headers: { 'Cache-Control': 'no-cache' }
      }),
      // Add members fetch
      authenticatedFetch(`/api/organizations/${organization.id}/members`, {
        headers: { 'Cache-Control': 'no-cache' }
      })
    ]);

    if (!questionsResponse.ok || !userResponsesResponse.ok || !statusResponse.ok || !membersResponse.ok) {
      throw new Error('One or more requests failed');
    }

    const [questions, userResponses, statusData, members] = await Promise.all([
      questionsResponse.json(),
      userResponsesResponse.json(),
      statusResponse.json(),
      membersResponse.json()
    ]);

    console.log('Loaded responses and members:', {
      userResponses,
      members,
      questions
    });

    if (!Array.isArray(questions)) {
      throw new Error('Invalid questions data structure');
    }

    // Process responses using question ID as key
    const responseMap = {};
    const teamResponseMap = {};

    // Process team responses first
    userResponses.forEach(response => {
      if (!response.question_id) return;
      
      if (!teamResponseMap[response.question_id]) {
        teamResponseMap[response.question_id] = [];
      }
      teamResponseMap[response.question_id].push(response);

      // If this is the current user's response, add it to responseMap
      if (response.user_id === user?.id) {
        responseMap[response.question_id] = {
          ...response,
          organization_response: response.organization_response
        };
      }
    });

    // Initialize temp responses
    const initialTempResponses = questions.reduce((acc, question) => {
      const existingResponse = responseMap[question.id];
      if (existingResponse?.response_value !== undefined) {
        acc[question.id] = existingResponse.response_value;
      } else {
        acc[question.id] = getDefaultValue(question.question_type, question.options);
      }
      return acc;
    }, {});

    // Process and prepare team members data
    const processedMembers = members.map(member => ({
      ...member,
      // Ensure we have a consistent user_id field
      user_id: member.user_id || member.id
    }));

    console.log('Final processed data:', {
      teamResponseMap,
      responseMap,
      initialTempResponses,
      processedMembers
    });

    // Update all state at once
    setQuestions(questions);
    setOrganizationResponses(responseMap);
    setTempResponses(initialTempResponses);
    setTeamMembers(processedMembers); // Set the processed members
    setTeamResponses(teamResponseMap);
    setHasCompletedOnce(statusData.hasCompleted);
    
    onUpdateResponses?.(responseMap);

    const hasResponses = Object.keys(responseMap).length > 0;
    if (startInReviewMode && hasResponses) {
      setShowReview(true);
    }

    hasInitialResponses.current = hasResponses;
    initialLoadComplete.current = true;
    loadAttempts.current += 1;
    
    console.log('Data load complete', {
      questionCount: questions.length,
      responseCount: Object.keys(responseMap).length,
      teamResponseCount: Object.keys(teamResponseMap).length,
      memberCount: processedMembers.length,
      hasResponses,
      loadAttempts: loadAttempts.current
    });

    setLoadState(LOAD_STATE.LOADED);

  } catch (error) {
    console.error('Error loading questionnaire data:', error);
    setError(`Failed to load questionnaire: ${error.message}`);
    setLoadState(LOAD_STATE.ERROR);
  }
}, [organization?.id, startInReviewMode, onUpdateResponses, loadState, user?.id]);

  // Initial data load effect
  useEffect(() => {
    if (organization?.id && loadState === LOAD_STATE.INITIAL) {
      loadData();
    }
  }, [organization?.id, loadData, loadState]);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);
  const BASE_PATH = '/api/core-questions';

  const canProgress = useMemo(() => {
    const currentQuestion = questions[currentIndex];
    if (!currentQuestion) return false;
    return !currentQuestion.required || 
           isResponseComplete(tempResponses[currentQuestion.id], currentQuestion);
}, [questions, currentIndex, tempResponses]);

  // Response saving handler
  const saveResponse = useCallback(async (questionId, organizationId, value, questionType) => {
    if (!questionId || !organizationId) {
      throw new Error('Question ID and Organization ID are required');
    }

    try {
      console.log('Saving team response:', {
        questionId,
        organizationId,
        value,
        questionType
      });

      const response = await authenticatedFetch(`${BASE_PATH}/responses/team`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          question_id: questionId,
          organization_id: organizationId,
          response_value: value
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to save team response: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result) {
        throw new Error('No response data received from server');
      }

      setTempResponses(prev => ({
        ...prev,
        [questionId]: result.response_value || value
      }));

      if (emitTeamResponse && result.id) {
        emitTeamResponse(questionId, result);
      }

      return result;
    } catch (error) {
      console.error('Error saving response:', error);
      throw error;
    }
  }, [emitTeamResponse]);

  // Response change handler
// In RequirementsQuestionnaire.jsx

const handleTempResponse = useCallback(async (value, question) => {
  if (!organization?.id) {
      console.error('Organization ID missing');
      return;
  }

  let processedValue;

  switch (question.question_type) {
      case 'multiple_choice':
          processedValue = Array.isArray(value) ? value : [value];
          break;

      case 'range':
          try {
              if (typeof value === 'object' && value !== null) {
                  const formattedValue = {
                      min: Number(value.min),
                      max: Number(value.max),
                      currency: value.currency || currency.symbol,
                      currencyCode: value.currencyCode || currency.code,
                      usdMin: value.usdMin || Math.round(Number(value.min) / CURRENCY_CONFIGS[value.currencyCode || 'USD'].scale),
                      usdMax: value.usdMax || Math.round(Number(value.max) / CURRENCY_CONFIGS[value.currencyCode || 'USD'].scale)
                  };
                  processedValue = JSON.stringify(formattedValue);
              } else if (typeof value === 'string') {
                  const parsed = JSON.parse(value);
                  processedValue = JSON.stringify(parsed);
              } else {
                  processedValue = null;
              }
          } catch (error) {
              console.error('Error processing range value:', error);
              processedValue = null;
          }
          break;

      case 'tristate_boolean':
          if (['YES', 'NO', 'DONT_KNOW'].includes(value)) {
              processedValue = value;
          }
          break;

      case 'boolean':
          processedValue = value === 'true' || value === true;
          break;

      default:
          processedValue = String(value);
  }

  setTempResponses(prev => ({
      ...prev,
      [question.id]: processedValue
  }));

  setValidationErrors(prev => ({
      ...prev,
      [question.id]: null
  }));

  try {
      // First save user response
      await saveResponse(
          question.id,
          organization.id,
          processedValue,
          question.question_type
      );

      // If user is admin, also save organization response
      if (userWithRole?.role === 'admin') {
          const response = await authenticatedFetch(`/api/core-questions/responses/organization`, {
              method: 'POST',
              headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
              },
              body: JSON.stringify({
                  question_id: question.id,
                  organization_id: organization.id,
                  response_value: processedValue,
                  locked_to_user_id: currentUser?.id
              })
          });

          if (!response.ok) {
              throw new Error('Failed to save organization response');
          }

          // Update organizationResponses state
          const orgResponse = await response.json();
          setOrganizationResponses(prev => ({
              ...prev,
              [question.id]: {
                  ...orgResponse,
                  response_value: processedValue
              }
          }));
      }
  } catch (error) {
      console.error('Error auto-saving response:', error);
      // Show error in UI if needed
      setValidationErrors(prev => ({
          ...prev,
          [question.id]: 'Failed to save response'
      }));
  }
}, [organization?.id, currency, saveResponse, userWithRole, currentUser?.id]);

  // Since handleNext and handleSkip depend on handleComplete, we need to define handleComplete first
  
  // Handle completion of the questionnaire
  const handleComplete = useCallback(async () => {
      // If we're on the last question and there's a response, save it
      if (currentIndex === questions.length - 1) {
          const currentQuestion = questions[currentIndex];
          if (currentQuestion && tempResponses[currentQuestion.id] !== undefined) {
              try {
                  const currentValue = tempResponses[currentQuestion.id];
                  const savedValue = organizationResponses[currentQuestion.id]?.response_value;
                  
                  // Only save if the response has changed
                  if (JSON.stringify(currentValue) !== JSON.stringify(savedValue)) {
                      await saveResponse(
                          currentQuestion.id, 
                          organization.id, 
                          currentValue, 
                          currentQuestion.question_type
                      );
                  }
              } catch (error) {
                  console.error('Error saving final response:', error);
              }
          }
      }
      
      // Show the review screen with all responses
      setShowReview(true);
      
      // Notify parent component that the questionnaire is complete
      onComplete?.();
  }, [
      currentIndex, 
      questions, 
      tempResponses, 
      organizationResponses, 
      saveResponse, 
      organization?.id, 
      onComplete, 
      setShowReview
  ]);
  
  // Navigate to the previous question
  const handlePrevious = useCallback(() => {
      if (currentIndex > 0) {
          setCurrentIndex(prev => prev - 1);
      }
  }, [currentIndex]);

  // Navigate to the next question
  const handleNext = useCallback(() => {
      if (currentIndex < questions.length - 1) {
          // Store the response if it's valid
          if (questions[currentIndex] && tempResponses[questions[currentIndex].id] !== undefined) {
              const questionId = questions[currentIndex].id;
              const value = tempResponses[questionId];
              const questionType = questions[currentIndex].question_type;
              
              try {
                  saveResponse(questionId, organization?.id, value, questionType);
              } catch (error) {
                  console.error('Error saving response during next:', error);
              }
          }
          
          setCurrentIndex(prev => prev + 1);
      } else {
          // Handle the last question by showing the review screen
          setShowReview(true);
          
          // If this is the last question, attempt to save it first
          const currentQuestion = questions[currentIndex];
          if (currentQuestion && tempResponses[currentQuestion.id] !== undefined) {
              try {
                  const questionId = currentQuestion.id;
                  const value = tempResponses[questionId];
                  const questionType = currentQuestion.question_type;
                  
                  saveResponse(questionId, organization?.id, value, questionType);
              } catch (error) {
                  console.error('Error saving final response:', error);
              }
          }
          
          // Notify parent of completion
          onComplete?.();
      }
  }, [currentIndex, questions, tempResponses, saveResponse, organization?.id, onComplete]);
  
  // Skip the current question and move to the next one
  const handleSkip = useCallback(() => {
      if (currentIndex < questions.length - 1) {
          // Mark the question as skipped if needed
          const questionId = questions[currentIndex]?.id;
          if (questionId) {
              // You might want to track skipped questions in your state
              console.log(`Question ${questionId} was skipped`);
          }
          
          // Move to the next question without saving a response
          setCurrentIndex(prev => prev + 1);
      } else {
          // If it's the last question, treat skip like complete
          setShowReview(true);
          onComplete?.();
      }
  }, [currentIndex, questions, onComplete]);

  // Determine if the current response is valid
  const isCurrentResponseValid = useMemo(() => {
      if (!questions || questions.length === 0 || currentIndex >= questions.length) {
          return false;
      }
      
      const currentQuestion = questions[currentIndex];
      if (!currentQuestion) {
          return false;
      }
      
      // If the question is not required, it's always valid
      if (!currentQuestion.required) {
          return true;
      }
      
      // Check if there's a response and if it's complete
      const response = tempResponses[currentQuestion.id];
      if (response === undefined) {
          return false;
      }
      
      return isResponseComplete(response, currentQuestion);
  }, [questions, currentIndex, tempResponses]);

  // Handle review mode
  const handleReview = useCallback(() => {
    setShowReview(true);
  }, []);

  // Handle going back to questions from review
  const handleBackToQuestions = useCallback(() => {
    setShowReview(false);
  }, []);

  // Handle adding new questions (placeholder function)
  const handleAddQuestion = useCallback(() => {
    console.log('Add question functionality not implemented yet');
    // TODO: Implement add question functionality
  }, []);

  // Questionnaire completion handler
  
  // Find the handleConfirm function that's called when completing the questionnaire
// Update the handleConfirm function to use the imported service
// In RequirementsQuestionnaire.jsx

// In RequirementsQuestionnaire.jsx

const handleConfirm = async () => {
  if (!organization?.id) {
      throw new Error('Organization data is missing. Please try again.');
  }

  try {
      // Create properly structured responses object
      const formattedResponses = questions.reduce((acc, question) => {
          const response = tempResponses[question.id];
          if (response !== undefined) {
              acc[question.id] = {
                  question_id: question.id,
                  response_value: response,
                  question_type: question.question_type
              };
          }
          return acc;
      }, {});

      // Debug logging
      console.log('Submitting responses:', {
          tempResponses,
          formattedResponses,
          questions: questions.map(q => ({
              id: q.id,
              type: q.question_type,
              required: q.required
          }))
      });

      // Validate with formatted responses
      const validation = await questionnaireService.validateResponses(
          organization.id,
          formattedResponses
      );

      if (!validation.isValid) {
          console.error('Validation failed:', validation);
          throw new Error(`Please answer all required questions (${validation.missingQuestions.length} remaining)`);
      }

      // Save with formatted responses
      await questionnaireService.saveAllReviewResponses(
          organization.id,
          formattedResponses,
          user?.role === 'admin'
      );

      // Mark questionnaire as complete
      await questionnaireService.markComplete(organization.id);

      // Call the completion callback
      onComplete?.();
  } catch (error) {
      console.error('Error in handleConfirm:', error);
      throw error;
  }
};

  // Team collaboration handlers
  const handleCommentAddCallback = useCallback(async (questionId, text) => {
    try {
      const comment = await authenticatedFetch(`${BASE_PATH}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question_id: questionId,
          organization_id: organization.id,
          comment_text: text
        })
      });

      if (!comment.ok) {
        throw new Error('Failed to add comment');
      }

      const commentData = await comment.json();
      setComments(prev => ({
        ...prev,
        [questionId]: [...(prev[questionId] || []), commentData]
      }));

      if (emitComment) {
        emitComment(questionId, commentData);
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      setError('Failed to add comment');
    }
  }, [organization.id, emitComment]);

// Update the reaction handler
// In RequirementsQuestionnaire.jsx

const handleReactionAddCallback = useCallback(async (commentId, emoji) => {
  try {
      console.log('Adding reaction:', { commentId, emoji });

      const response = await authenticatedFetch('/api/core-questions/reactions/comments', {
          method: 'POST',
          headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
          },
          body: JSON.stringify({
              comment_id: commentId,
              emoji: emoji // This should now be the actual emoji character
          })
      });

      if (!response.ok) {
          const errorData = await response.json();
          console.error('Reaction error:', errorData);
          throw new Error(errorData.error || 'Failed to add reaction');
      }

      const reactionData = await response.json();
      console.log('Reaction added successfully:', reactionData);

      // Update local state
      setReactions(prev => {
          const updatedReactions = { ...prev };
          const commentReactions = Array.isArray(updatedReactions[commentId]) 
              ? [...updatedReactions[commentId]] 
              : [];

          if (reactionData.removed) {
              return {
                  ...updatedReactions,
                  [commentId]: commentReactions.filter(r => 
                      !(r.user_id === currentUser.id && r.emoji === emoji)
                  )
              };
          } else {
              return {
                  ...updatedReactions,
                  [commentId]: [...commentReactions, reactionData]
              };
          }
      });

      if (emitReaction) {
          emitReaction(commentId, reactionData);
      }

  } catch (error) {
      console.error('Error adding reaction:', error);
      throw error;
  }
}, [currentUser?.id, emitReaction]);

  const handleLockToUserCallback = useCallback(async (questionId, userId) => {
    try {
      if (!emitLock) {
        throw new Error('Socket not available for locking');
      }

      await emitLock(questionId, userId);
      setLockedResponses(prev => ({
        ...prev,
        [questionId]: userId
      }));
    } catch (error) {
      console.error('Error locking response:', error);
      setError('Failed to lock response');
    }
  }, [emitLock]);

  // Memoized derived state
  const currentQuestion = useMemo(() =>
    questions[currentIndex],
    [questions, currentIndex]
  );

  const canShowReview = useMemo(() =>
    questions.every(question =>
        !question.required || // Skip check for non-required questions
        (tempResponses[question.id] !== undefined && 
         isResponseComplete(tempResponses[question.id], question))
    ),
    [questions, tempResponses]
);

// In RequirementsQuestionnaire.jsx


useEffect(() => {
  const loadReactions = async () => {
      if (!currentQuestion?.id || !organization?.id) return;

      try {
          // Ensure comments exist and is an array
          if (!questionComments[currentQuestion.id] || 
              !Array.isArray(questionComments[currentQuestion.id])) {
              console.log('No comments available for reactions:', {
                  questionId: currentQuestion.id,
                  comments: questionComments[currentQuestion.id]
              });
              return;
          }

          const currentComments = questionComments[currentQuestion.id];
          
          // Load reactions for visible comments
          const reactionPromises = currentComments.map(async comment => {
              const response = await authenticatedFetch(
                  `/api/core-questions/reactions/comments/${comment.id}`
              );
              if (!response.ok) throw new Error('Failed to fetch reactions');
              const reactionData = await response.json();
              return { commentId: comment.id, reactions: reactionData };
          });

          const allReactions = await Promise.all(reactionPromises);
          
          // Transform into the expected format
          const groupedReactions = allReactions.reduce((acc, { commentId, reactions }) => {
              acc[commentId] = reactions;
              return acc;
          }, {});

          console.log('Loaded reactions:', {
              questionId: currentQuestion.id,
              commentCount: currentComments.length,
              reactions: groupedReactions
          });
          
          setReactions(groupedReactions);
      } catch (error) {
          console.error('Error loading reactions:', error);
      }
  };

  loadReactions();
}, [currentQuestion?.id, organization?.id, questionComments]);


// Add this effect to load comments
useEffect(() => {
  const loadComments = async () => {
      if (!currentQuestion?.id || !organization?.id) return;

      console.log('Loading comments for:', {
        questionId: currentQuestion.id,
        organizationId: organization.id
    });
      
      try {
          const response = await authenticatedFetch(
              `/api/core-questions/comments/${organization.id}/${currentQuestion.id}`
          );
          
          if (!response.ok) {
              throw new Error('Failed to fetch comments');
          }
          
          const comments = await response.json();
          console.log('Loaded comments:', comments);

          setQuestionComments(prev => ({
              ...prev,
              [currentQuestion.id]: comments
          }));
      } catch (error) {
          console.error('Error loading comments:', error);
      }
  };

  loadComments();
}, [currentQuestion?.id, organization?.id]);

// Update the reviewResponses memoized value
const reviewResponses = useMemo(() => {
  if (!showReview) return null;
  return questions.reduce((acc, question) => {
      const userResponse = tempResponses[question.id];
      const orgResponse = organizationResponses[question.id];
      
      acc[question.id] = {
          question_type: question.question_type,
          response_value: userResponse, // May be undefined for skipped questions
          organization_response: orgResponse?.organization_response,
          question_text: question.question_text,
          required: question.required,
          skipped: userResponse === undefined
      };
      return acc;
  }, {});
}, [showReview, questions, tempResponses, organizationResponses]);

// If ReviewMode is being shown
if ((showReview || (startInReviewMode && hasInitialResponses.current)) && reviewResponses) {
  if (!userWithRole?.role) {
      return (
          <div className="flex items-center justify-center min-h-screen">
              <div className="text-lg">Loading user role...</div>
          </div>
      );
  }

  return (
      <ReviewMode
          questions={questions}
          responses={reviewResponses}
          onEditQuestion={handleBackToQuestions}
          onConfirm={handleConfirm}
          saving={saving}
          currency={currency}
          organization={organization}
          user={userWithRole}
      />
  );
}

  // UI renders
  if (loadState === LOAD_STATE.LOADING || loadState === LOAD_STATE.INITIAL) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-lg">Loading questions...</div>
      </div>
    );
  }

  if (loadState === LOAD_STATE.ERROR || error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <AlertCircle className="w-8 h-8 text-red-500" />
        <div className="text-lg text-red-600">{error}</div>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if ((showReview || (startInReviewMode && hasInitialResponses.current)) && reviewResponses) {
    if (!userWithRole?.role) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg">Loading user role...</div>
            </div>
        );
    }

    return (
        <ReviewMode
            questions={questions}
            responses={reviewResponses}
            onEditQuestion={handleBackToQuestions}
            onConfirm={handleConfirm}
            saving={saving}
            currency={currency}
            organization={organization}
            user={userWithRole}
        />
    );
}


  if (!currentQuestion) return null;

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
        <ProgressIndicator
            currentIndex={currentIndex}
            totalQuestions={questions.length}
        />

        {!hideTeamSection ? (
            <TeamQuestionnaireDisplay
                question={currentQuestion}
                organizationResponse={organizationResponses[currentQuestion.id]}
                userResponses={teamResponses[currentQuestion.id] || []}
                tempValue={tempResponses[currentQuestion.id]}
                reactions={reactions}
                currentUser={userWithRole}
                teamMembers={teamMembers}
                error={validationErrors[currentQuestion.id]}
                saving={saving}
                currency={currency}
                onResponseChange={(value) => handleTempResponse(value, currentQuestion)}
                onCommentAdd={(text) => handleCommentAddCallback(currentQuestion.id, text)}
                onReactionAdd={(responseId, emoji) =>
                    handleReactionAddCallback(responseId, currentQuestion.id, emoji)}
                onLockToUser={(userId) => handleLockToUserCallback(currentQuestion.id, userId)}
                organization={organization}
                currentIndex={currentIndex}
                totalQuestions={questions.length}
                onPrevious={handlePrevious}
                onNext={handleNext}
                onReview={handleReview}
                comments={questionComments[currentQuestion.id] || []}
                canProgress={!currentQuestion.required ||
                    isResponseComplete(tempResponses[currentQuestion.id], currentQuestion)}
                showReviewButton={canShowReview}
            />
        ) : (
            // Simplified display without team section
            <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold mb-4">{currentQuestion.title}</h3>
                {currentQuestion.description && (
                    <p className="text-gray-600 mb-6">{currentQuestion.description}</p>
                )}
                
                {renderQuestionInput(
                    currentQuestion,
                    tempResponses[currentQuestion.id],
                    validationErrors[currentQuestion.id],
                    handleTempResponse
                )}
            </div>
        )}

        {hideTeamSection ? (
            <SimpleNavigation
                currentIndex={currentIndex}
                totalQuestions={questions.length}
                onPrevious={handlePrevious}
                onNext={handleNext}
                onSkip={handleSkip}
                onComplete={handleComplete}
                isValid={isCurrentResponseValid}
                isSkippable={currentQuestion.isOptional}
                isLastQuestion={currentIndex === questions.length - 1}
            />
        ) : (
            <Navigation
                currentIndex={currentIndex}
                totalQuestions={questions.length}
                onPrevious={handlePrevious}
                onNext={handleNext}
                onSkip={handleSkip}
                onComplete={handleComplete}
                onAddQuestion={handleAddQuestion}
                onReview={handleReview}
                canAddQuestions={Boolean(userWithRole?.permissions?.canAddQuestions)}
                reviewMode={startInReviewMode}
                isValid={isCurrentResponseValid}
                isSkippable={currentQuestion.isOptional}
                isLastQuestion={currentIndex === questions.length - 1}
            />
        )}
    </div>
);
};

// PropTypes validation
    RequirementsQuestionnaire.propTypes = {
        organization: PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            name: PropTypes.string
        }).isRequired,
        onComplete: PropTypes.func,
        onUpdateResponses: PropTypes.func,
        startInReviewMode: PropTypes.bool,
        initialResponses: PropTypes.object,
        user: PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            email: PropTypes.string,
            name: PropTypes.string
        }).isRequired,
        hideTeamSection: PropTypes.bool
    };

// Add this function after the fetchTeamAndResponses function
const renderQuestionInput = (question, value, error, onChange) => {
    // Debug the entire question object
    console.log('Current question structure:', JSON.stringify(question, null, 2));
    
    // Add debug mode flag - change to true to show debug info in UI
    const DEBUG_MODE = false;
    
    // Debug display of question structure
    const renderDebugInfo = () => {
        if (!DEBUG_MODE) return null;
        
        return (
            <div className="bg-gray-100 p-3 mb-4 rounded text-xs">
                <h4 className="font-bold mb-2">Debug Question Structure:</h4>
                <pre className="whitespace-pre-wrap overflow-auto max-h-40">
                    {JSON.stringify(question, null, 2)}
                </pre>
            </div>
        );
    };
    
    const getOptions = () => {
        // For debugging
        console.log('Question options data:', question.options);
        
        // If options is null or undefined, return empty array
        if (!question.options) {
            console.log('No options found');
            return [];
        }
        
        // If options is already an array, use it directly
        if (Array.isArray(question.options)) {
            console.log('Options is an array:', question.options);
            // Check if items have value and label properties
            if (question.options.length > 0) {
                if (typeof question.options[0] === 'object') {
                    // Array of objects - check for expected properties
                    return question.options;
                } else if (typeof question.options[0] === 'string') {
                    // Array of strings - convert to value/label objects
                    return question.options.map(opt => ({
                        value: opt,
                        label: opt
                    }));
                }
            }
            return question.options;
        }
        
        // If options has a choices property
        if (question.options.choices) {
            console.log('Options has choices property:', question.options.choices);
            
            // If choices is an array
            if (Array.isArray(question.options.choices)) {
                // Check if array items are objects or simple values
                if (question.options.choices.length > 0) {
                    if (typeof question.options.choices[0] === 'object') {
                        return question.options.choices;
                    } else if (typeof question.options.choices[0] === 'string') {
                        // Array of strings - convert to value/label objects
                        return question.options.choices.map(opt => ({
                            value: opt,
                            label: opt
                        }));
                    }
                }
                return question.options.choices;
            }
            
            // If choices is a string, try to parse it
            if (typeof question.options.choices === 'string') {
                try {
                    const parsed = JSON.parse(question.options.choices);
                    console.log('Parsed choices:', parsed);
                    
                    // If parsed result is an array
                    if (Array.isArray(parsed)) {
                        if (parsed.length > 0) {
                            if (typeof parsed[0] === 'object') {
                                return parsed;
                            } else if (typeof parsed[0] === 'string') {
                                // Array of strings - convert to value/label objects
                                return parsed.map(opt => ({
                                    value: opt,
                                    label: opt
                                }));
                            }
                        }
                        return parsed;
                    }
                    
                    return [];
                } catch (e) {
                    console.error('Error parsing choices:', e);
                    // Try to handle as comma-separated list
                    if (question.options.choices.includes(',')) {
                        const items = question.options.choices.split(',').map(item => item.trim());
                        return items.map(item => ({ value: item, label: item }));
                    }
                    return [];
                }
            }
        }
        
        // If we got here, try to handle options as a direct object with potential formats
        console.log('Trying to handle options as direct object');
        
        // Case: options might have direct key-value pairs (common in some APIs)
        const optionsArray = [];
        for (const key in question.options) {
            if (key !== 'choices' && typeof question.options[key] !== 'function') {
                optionsArray.push({
                    value: key,
                    label: question.options[key]
                });
            }
        }
        
        if (optionsArray.length > 0) {
            console.log('Created options array from object properties:', optionsArray);
            return optionsArray;
        }
        
        // Fallback: return empty array if we couldn't find a valid options structure
        console.log('Could not determine options structure, returning empty array');
        return [];
    };
    
    // Use the appropriate input based on question type
    switch (question.question_type) {
        case 'text':
            return (
                <div>
                    {renderDebugInfo()}
                    <input
                        type="text"
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value, question)}
                        placeholder={question.placeholder || 'Enter your answer'}
                        className={`w-full px-4 py-2 border rounded-md ${
                            error ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
                </div>
            );
        case 'textarea':
            return (
                <div>
                    {renderDebugInfo()}
                    <textarea
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value, question)}
                        placeholder={question.placeholder || 'Enter your answer'}
                        rows={5}
                        className={`w-full px-4 py-2 border rounded-md ${
                            error ? 'border-red-500' : 'border-gray-300'
                        }`}
                    />
                    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
                </div>
            );
        case 'select':
            const selectOptions = getOptions();
            console.log('Select options:', selectOptions);
            return (
                <div>
                    {renderDebugInfo()}
                    <select
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value, question)}
                        className={`w-full px-4 py-2 border rounded-md ${
                            error ? 'border-red-500' : 'border-gray-300'
                        }`}
                    >
                        <option value="">Select an option</option>
                        {selectOptions.map((option, index) => {
                            console.log(`Rendering select option ${index}:`, option);
                            return (
                                <option 
                                    key={option.value || `option-${index}`} 
                                    value={option.value || ''}
                                >
                                    {option.label || option.value || `Option ${index + 1}`}
                                </option>
                            );
                        })}
                    </select>
                    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
                </div>
            );
        case 'radio':
            const radioOptions = getOptions();
            console.log('Radio options:', radioOptions);
            return (
                <div>
                    {renderDebugInfo()}
                    <div className="space-y-2">
                        {radioOptions.length === 0 ? (
                            <p className="text-yellow-500">No options available</p>
                        ) : (
                            radioOptions.map((option, index) => {
                                console.log(`Rendering radio option ${index}:`, option);
                                return (
                                    <label 
                                        key={option.value || `option-${index}`} 
                                        className="flex items-center"
                                    >
                                        <input
                                            type="radio"
                                            checked={value === option.value}
                                            onChange={() => onChange(option.value, question)}
                                            className="mr-2"
                                        />
                                        <span>{option.label || option.value || `Option ${index + 1}`}</span>
                                    </label>
                                );
                            })
                        )}
                    </div>
                    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
                </div>
            );
        case 'checkbox':
        case 'multiple_choice':
            const checkOptions = getOptions();
            console.log('Checkbox options:', checkOptions);
            console.log('Current value:', value);
            return (
                <div>
                    {renderDebugInfo()}
                    <div className="space-y-2">
                        {checkOptions.length === 0 ? (
                            <div>
                                <p className="text-yellow-500 mb-2">Debug: Options data not properly structured</p>
                                {/* Always show some checkboxes for testing */}
                                {['Option 1', 'Option 2', 'Option 3', 'Option 4'].map((label, idx) => (
                                    <label key={idx} className="flex items-center mb-2">
                                        <input
                                            type="checkbox"
                                            checked={Array.isArray(value) && value.includes(`debug-${idx}`)}
                                            onChange={(e) => {
                                                const newValue = Array.isArray(value) ? [...value] : [];
                                                if (e.target.checked) {
                                                    newValue.push(`debug-${idx}`);
                                                } else {
                                                    const index = newValue.indexOf(`debug-${idx}`);
                                                    if (index !== -1) {
                                                        newValue.splice(index, 1);
                                                    }
                                                }
                                                onChange(newValue, question);
                                            }}
                                            className="mr-2"
                                        />
                                        <span>{label}</span>
                                    </label>
                                ))}
                            </div>
                        ) : (
                            checkOptions.map((option, index) => {
                                console.log(`Rendering checkbox option ${index}:`, option);
                                // Ensure we have a value property, fallback to index if needed
                                const optionValue = option.value !== undefined ? option.value : `option-${index}`;
                                return (
                                    <label 
                                        key={optionValue} 
                                        className="flex items-center"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={Array.isArray(value) && value.includes(optionValue)}
                                            onChange={(e) => {
                                                const newValue = Array.isArray(value) ? [...value] : [];
                                                if (e.target.checked) {
                                                    newValue.push(optionValue);
                                                } else {
                                                    const index = newValue.indexOf(optionValue);
                                                    if (index !== -1) {
                                                        newValue.splice(index, 1);
                                                    }
                                                }
                                                onChange(newValue, question);
                                            }}
                                            className="mr-2"
                                        />
                                        <span>{option.label || option.value || `Option ${index + 1}`}</span>
                                    </label>
                                );
                            })
                        )}
                    </div>
                    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
                </div>
            );
        default:
            return (
                <div>
                    {renderDebugInfo()}
                    <p className="text-red-500">
                        Question type {question.question_type || 'undefined'} not supported
                    </p>
                </div>
            );
    }
};

// Simple Navigation component for the simplified view
const SimpleNavigation = ({ 
    currentIndex, 
    totalQuestions, 
    onPrevious, 
    onNext, 
    onSkip, 
    onComplete, 
    isValid, 
    isSkippable, 
    isLastQuestion 
}) => {
    return (
        <div className="flex justify-between items-center mt-6">
            <button
                onClick={onPrevious}
                disabled={currentIndex === 0}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                    currentIndex === 0
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
                Previous
            </button>
            
            <div className="text-sm text-gray-500">
                Question {currentIndex + 1} of {totalQuestions}
            </div>
            
            <div className="flex space-x-2">
                {isSkippable && (
                    <button
                        onClick={onSkip}
                        className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700"
                    >
                        Skip
                    </button>
                )}
                
                <button
                    onClick={isLastQuestion ? onComplete : onNext}
                    disabled={!isValid && !isSkippable}
                    className={`px-4 py-2 text-sm font-medium rounded-md ${
                        !isValid && !isSkippable
                            ? 'bg-green-200 text-green-400 cursor-not-allowed'
                            : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                >
                    {isLastQuestion ? 'Complete' : 'Next'}
                </button>
            </div>
        </div>
    );
};

export default RequirementsQuestionnaire;